import {FlightLeg} from '../../../../../schema';
import {prepareDepartureChange} from '../../route-edit';
import {DetailsValues, selectionID} from '../state/values';
import {detailsPatch} from './input';

type DetailsFlight = Parameters<typeof prepareDepartureChange>[0] & {_id: string};

/** Dependencies for one validated details save; the owning form supplies confirmation and persistence. */
export interface SaveDetailsOptions {
    flight: DetailsFlight;
    values: DetailsValues;
    tenant: {tpID: string; customerID: string; orgID: string};
    isCurrent: () => boolean;
    confirmDeparture: (departure: {_id: string; name: string}) => Promise<boolean>;
    reviewDeparture: (legs: FlightLeg[]) => Promise<FlightLeg[] | undefined>;
    onDepartureBusyChange: (busy: boolean) => void;
    mutate: (variables: {payload: DetailsValues; orgID: string}) => Promise<unknown>;
}

/**
 * Saves a changed-field patch without rebuilding assignments for ordinary detail edits.
 * Departure changes retain their exclusive confirmation/review flow. Cancelling that
 * flow discards only the departure edit; other validated fields can still be saved.
 */
export async function saveDetails(options: SaveDetailsOptions) {
    const {flight, values} = options;
    const patch = detailsPatch(values);
    const cancelledKeys: string[] = [];
    const departureID = selectionID(values.departureID);

    if (departureID && departureID !== flight.departureID?._id) {
        const selection = values.departureID as {label?: string};
        const departure = {_id: departureID, name: selection.label || departureID};
        options.onDepartureBusyChange(true);
        try {
            const update = await prepareDepartureChange(
                flight,
                departure,
                options.reviewDeparture,
                () => options.confirmDeparture(departure)
            );
            if (!update) {
                cancelledKeys.push('departureID');
            } else {
                Object.assign(patch, update.patch);
            }
        } finally {
            options.onDepartureBusyChange(false);
        }
    }

    if (!options.isCurrent()) {
        throw new Error('The flight changed. Review your changes before saving again.');
    }
    const saved = Object.keys(patch).length > 0;
    if (saved) {
        await options.mutate({
            payload: {
                _id: flight._id,
                tpID: options.tenant.tpID,
                customerID: options.tenant.customerID,
                ...patch,
            },
            orgID: options.tenant.orgID,
        });
    }
    return {cancelledKeys, saved};
}
